import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WIZARD_CONTROLLER } from '@mwe/features/wizard';
import { PopupService } from '@mwe/services';
import { UmmeldungStore } from './ummeldung.store';
import { EditInputTarget, UnterschreibendePartei } from './models';
import {
  ROUTE_UMMELDUNG_CURRENT_CONTRACT_PARTNER,
  ROUTE_UMMELDUNG_NEW_CONTRACT_PARTNER,
  ROUTE_UMMELDUNG_PRODUCT,
  ROUTE_UMMELDUNG_WIZARD,
} from '../../app.constants';
import { UMMELDUNG_EDIT_INPUTS_POPUP } from '../../config/popup/ummeldung-popup.config';
import { stepFormOf } from './utils';

const EDIT_INPUTS_CONFIG: Record<EditInputTarget, { route: string; revokedUnterschriften: UnterschreibendePartei[] }> = {
  produktUndDatum: { route: ROUTE_UMMELDUNG_PRODUCT, revokedUnterschriften: ['bisherigerKunde', 'neuerKunde'] },
  bisherigerVertragspartner: { route: ROUTE_UMMELDUNG_CURRENT_CONTRACT_PARTNER, revokedUnterschriften: ['bisherigerKunde'] },
  neuerVertragspartner: { route: ROUTE_UMMELDUNG_NEW_CONTRACT_PARTNER, revokedUnterschriften: ['neuerKunde'] },
};

/** "Eingaben ändern" of a signed step. Provided per Ummeldung wizard, see `UmmeldungWizardModule`. */
@Injectable()
export class UmmeldungEditInputsService {
  private readonly router = inject(Router);
  private readonly popupService = inject(PopupService);
  private readonly store = inject(UmmeldungStore);
  private readonly wizardController = inject(WIZARD_CONTROLLER);

  /**
   * A correction needs new signatures, so this asks first, then revokes the affected signatures and
   * opens the step to correct — Produktdaten always at step 1. Called from a step's frozen state, that
   * step's draft drops its signature too if it is among the revoked ones, since a fresh one is not in the
   * store yet.
   */
  async editInputs(target: EditInputTarget): Promise<void> {
    const confirmed = await this.popupService.tryOpen<boolean>({
      ...UMMELDUNG_EDIT_INPUTS_POPUP,
      messageKey: `ummeldung.popup.editInputs.message.${target}`,
    });
    if (!confirmed) {
      return;
    }

    const { route, revokedUnterschriften } = EDIT_INPUTS_CONFIG[target];
    this.store.revokeUnterschriften(revokedUnterschriften);
    const currentStepContext = this.wizardController.currentStep()?.context;
    if (currentStepContext) {
      stepFormOf(currentStepContext)?.openForEditInputs(revokedUnterschriften);
    }
    await this.router.navigate([ROUTE_UMMELDUNG_WIZARD, route]);
  }
}
